import { allSelectOptions } from "./constants";

export type OffenseMenuItem =
  | {
      id: string;
      label: string;
      children?: OffenseMenuItem[];
    }
  | undefined;

export type OffenseSelection = {
  field_name: string;
  sub_categories?: string[];
};

const ALL_OFFENSES_SUFFIX = "any";

const allOffensesItem = (category: OffenseMenuItem) =>
  category?.children?.find((child) =>
    child?.id.endsWith(`_${ALL_OFFENSES_SUFFIX}`)
  );

export const isMultiSelectCategory = (category: OffenseMenuItem) =>
  Boolean(allOffensesItem(category));

const categoryPrefix = (category: OffenseMenuItem) =>
  allOffensesItem(category)?.id.slice(0, -ALL_OFFENSES_SUFFIX.length) ?? "";

export const offenseOptions = (category: OffenseMenuItem) => {
  const allItem = allOffensesItem(category);
  return (category?.children ?? []).filter(
    (child): child is NonNullable<OffenseMenuItem> =>
      Boolean(child) && child !== allItem
  );
};

export const checkedOffenseIds = (
  category: OffenseMenuItem,
  fieldName: string,
  subCategories?: string[]
): string[] => {
  const allItem = allOffensesItem(category);
  const prefix = categoryPrefix(category);
  if (!allItem || !fieldName.startsWith(prefix)) return [];

  if (fieldName !== allItem.id) return [fieldName];
  if (!subCategories?.length)
    return offenseOptions(category).map((option) => option.id);
  return subCategories.map((subCategory) => prefix + subCategory);
};

export const offenseSelection = (
  category: OffenseMenuItem,
  checkedIds: string[],
  fieldNameWhenEmpty: string
): OffenseSelection => {
  const allItem = allOffensesItem(category);
  const prefix = categoryPrefix(category);
  const orderedIds = offenseOptions(category)
    .map((option) => option.id)
    .filter((id) => checkedIds.includes(id));

  if (!allItem || orderedIds.length === 0)
    return { field_name: fieldNameWhenEmpty };
  if (orderedIds.length === offenseOptions(category).length)
    return { field_name: allItem.id };
  if (orderedIds.length === 1) return { field_name: orderedIds[0] };

  return {
    field_name: allItem.id,
    sub_categories: orderedIds.map((id) => id.slice(prefix.length))
  };
};

export const offenseSelectionNames = (
  menuItems: OffenseMenuItem[],
  fieldName: string,
  subCategories?: string[]
): string | undefined => {
  if (!subCategories || subCategories.length < 2) return undefined;

  const category = menuItems.find(
    (item) => allOffensesItem(item)?.id === fieldName
  );
  if (!category) return undefined;

  const checkedIds = checkedOffenseIds(category, fieldName, subCategories);
  const names = offenseOptions(category)
    .filter((option) => checkedIds.includes(option.id))
    .map((option) => option.label.replace(/ offenses$/i, ""));
  if (names.length === 0) return undefined;

  return `${names.join(", ")} offenses`;
};

export const offenseSelectionLabel = (
  menuItems: OffenseMenuItem[],
  fieldName: string,
  subCategories?: string[]
): string | undefined => {
  if (subCategories && subCategories.length > 1) {
    const category = menuItems.find(
      (item) => allOffensesItem(item)?.id === fieldName
    );
    if (category)
      return `${subCategories.length} ${category.label.toLowerCase()}`;
  }
  return allSelectOptions[fieldName];
};
